import { BarChart3 } from "lucide-react";
import { ManualeLayout, Scheda, P, Steps, Bullets, Callout, FAQ } from "@/pages/manuali/ManualeLayout";

const A = "#7C3AED";
const SEZIONI = [
  { id: "intro", titolo: "Cos'è e a chi è riservata" },
  { id: "fonti", titolo: "Le due fonti: Foglio Cassa e Portafoglio" },
  { id: "fc", titolo: "Vista Foglio Cassa (incassato)" },
  { id: "pf", titolo: "Vista Portafoglio (polizze in essere)" },
  { id: "vivo", titolo: "Portafoglio vivo (esclusioni)" },
  { id: "grafici", titolo: "Grafici, tabelle ed export" },
  { id: "import", titolo: "Import dei dati" },
  { id: "mappa", titolo: "Mappatura rami" },
  { id: "faq", titolo: "Domande frequenti" },
];

export default function ManualeDirezione() {
  return (
    <ManualeLayout titolo="Dashboard Direzionale" sottotitolo="Analisi di produzione, incassato e portafoglio per la Direzione" icona={BarChart3} accent={A} sezioni={SEZIONI}>
      <Scheda id="intro" n={1} titolo="Cos'è e a chi è riservata" accent={A}>
        <P>La Dashboard Direzionale fornisce KPI, grafici e tabelle sulla produzione dell'agenzia. È uno strumento riservato: possono accedervi l'amministratore e le persone espressamente autorizzate. I dati per singolo collaboratore sono visibili solo a questa platea.</P>
      </Scheda>

      <Scheda id="fonti" n={2} titolo="Le due fonti: Foglio Cassa e Portafoglio" accent={A}>
        <P>In alto a destra trovi un selettore di fonte:</P>
        <Bullets items={[
          "Foglio Cassa: l'incassato (i titoli quietanzati) generato dal gestionale Assieasy. È un flusso, letto per periodo.",
          "Portafoglio: la fotografia delle polizze in essere (stock), con il premio annuo di ogni polizza.",
        ]} />
        <Callout tipo="info">Le due viste rispondono a domande diverse: quanto ho incassato (Foglio Cassa) e quanto vale il portafoglio in essere (Portafoglio).</Callout>
      </Scheda>

      <Scheda id="fc" n={3} titolo="Vista Foglio Cassa (incassato)" accent={A}>
        <P>Metrica selezionabile con l'interruttore <b>Lordo</b> (Premio) o <b>Netto</b> (Imponibile). Il periodo si basa su Data Effetto o Data Incasso.</P>
        <Steps items={[
          "Scegli metrica (Lordo/Netto) e base periodo (Effetto/Incasso).",
          "Applica i filtri: compagnia, collaboratore, tipo operazione, macro-ramo, nicchia, modalità, prodotto, comune, targa, o usa la ricerca libera.",
          "Leggi i KPI (produzione, provvigioni, RCA/RE/Vita) e le tabelle per compagnia, collaboratore, prodotto, ramo e tipo operazione.",
        ]} />
      </Scheda>

      <Scheda id="pf" n={4} titolo="Vista Portafoglio (polizze in essere)" accent={A}>
        <P>La metrica è l'<b>Imponibile annuo attuale</b> (il premio annuo consistente su tutte le compagnie). Di default vengono mostrate solo le <b>polizze attive</b>; puoi scegliere Annullate o Tutte.</P>
        <Bullets items={[
          "Filtri: compagnia, collaboratore, macro-ramo, nicchia, provincia, regione, frazionamento, ramo, prodotto, targa, scadenza (da/a), ricerca libera.",
          "KPI: portafoglio annuo, RCA/RE/Vita, numero province.",
          "Tabelle a schede: compagnia, collaboratore, prodotto, ramo, provincia, frazionamento e scadenze (per mese).",
        ]} />
        <Callout tipo="warn">Il comparto Auto è classificato sotto RCA (nicchie "R.C. Auto" e "Auto (comparto)"): il file non separa le garanzie accessorie. Puoi correggere le assegnazioni dalla Mappatura rami.</Callout>
      </Scheda>

      <Scheda id="vivo" n={5} titolo="Portafoglio vivo (esclusioni)" accent={A}>
        <P>In cima ai filtri del Portafoglio ci sono due interruttori, utili per una lettura del solo portafoglio ricorrente. Restano attivi per tutta la sessione.</P>
        <Bullets items={[
          "Escludi Cauzioni: toglie le polizze cauzione/fideiussione.",
          "Escludi Temporanee / Premio unico: toglie le polizze a frazionamento UNICO o TEMPORANEO e i premi unici (non ricorrenti).",
        ]} />
        <Callout tipo="info">Attivando entrambi ottieni il "portafoglio vivo": la base dei premi che si rinnovano e alimentano la crescita.</Callout>
      </Scheda>

      <Scheda id="grafici" n={6} titolo="Grafici, tabelle ed export" accent={A}>
        <P>Ogni vista mostra grafici (per compagnia, macro-ramo, collaboratore e, nel Portafoglio, per provincia). Puoi scaricare:</P>
        <Bullets items={[
          "Scarica Excel: esporta le righe filtrate in un file .xlsx.",
          "Grafici PNG: salva l'immagine dei grafici correnti.",
        ]} />
      </Scheda>

      <Scheda id="import" n={7} titolo="Import dei dati" accent={A}>
        <Steps items={[
          "Premi 'Importa Foglio Cassa' o 'Importa Portafoglio' a seconda della vista.",
          "Seleziona il file esportato dal gestionale (CSV o XLSX).",
          "L'import sostituisce lo snapshot precedente e aggiorna KPI, grafici e tabelle.",
        ]} />
        <Callout tipo="warn">L'import è una sostituzione completa: carica sempre il file aggiornato e completo (tutte le compagnie/polizze).</Callout>
      </Scheda>

      <Scheda id="mappa" n={8} titolo="Mappatura rami" accent={A}>
        <P>Dal pulsante "Mappatura rami" puoi correggere la categoria (macro e nicchia) assegnata a un ramo. Le modifiche si riflettono subito su KPI e grafici e valgono sia per Foglio Cassa sia per Portafoglio.</P>
      </Scheda>

      <Scheda id="faq" n={9} titolo="Domande frequenti" accent={A}>
        <FAQ items={[
          { q: "Perché nel Portafoglio non c'è il Lordo?", a: "Nell'export il campo Lordo annuo è valorizzato solo per alcune compagnie; per coerenza si usa l'Imponibile annuo attuale, presente su quasi tutte le polizze." },
          { q: "I filtri Escludi restano attivi?", a: "Sì, restano memorizzati per tutta la sessione, anche cambiando fonte o rientrando nella dashboard." },
          { q: "Come stampo o salvo in PDF questo manuale?", a: "Usa il pulsante 'Stampa / Salva PDF' in alto a destra." },
        ]} />
      </Scheda>
    </ManualeLayout>
  );
}
